/**
 * useProfile.ts
 *
 * Hook for working with user profile data and settings
 *
 * Primary responsibilities:
 * - Provide access to profile data
 * - Handle profile updates and settings
 * - Manage API keys and integrations
 */

import { SetStateAction, useCallback, useContext } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { toast } from 'sonner'
import {
  logoutFromClient,
  redirectToLoginAfterLogout
} from '@/lib/auth/client-auth-state'
import { supabase } from '@/lib/supabase/browser-client'
import { ChatbotUIContext } from '@/context/context'
import {
  profileAtom,
  displayNameAtom,
  profileImageUrlAtom,
  isProfileSettingsOpenAtom,
  apiKeysAtom
} from '@/atoms/profileAtoms'
import { getAvailableAPIKeys } from '@/atoms/modelsAtoms'
import { Tables } from '@/supabase/types'
import { updateProfile } from '@/db/profile'

const resolveStateAction = <Value,>(
  value: SetStateAction<Value>,
  previous: Value
): Value => {
  return typeof value === 'function'
    ? (value as (current: Value) => Value)(previous)
    : value
}

export function useProfile() {
  const context = useContext(ChatbotUIContext)
  const [atomProfile, setAtomProfile] = useAtom(profileAtom)
  const [atomDisplayName, setAtomDisplayName] = useAtom(displayNameAtom)
  const [atomProfileImageUrl, setAtomProfileImageUrl] =
    useAtom(profileImageUrlAtom)
  const [atomIsProfileSettingsOpen, setAtomIsProfileSettingsOpen] =
    useAtom(isProfileSettingsOpenAtom)
  const atomApiKeys = useAtomValue(apiKeysAtom)

  const shouldUseContext = context.isContextInitialized

  const profile = shouldUseContext ? context.profile : atomProfile
  const setProfile = shouldUseContext ? context.setProfile : setAtomProfile
  const isProfileSettingsOpen = shouldUseContext
    ? context.isProfileSettingsOpen
    : atomIsProfileSettingsOpen
  const setIsProfileSettingsOpen = shouldUseContext
    ? context.setIsProfileSettingsOpen
    : setAtomIsProfileSettingsOpen

  const displayName = shouldUseContext
    ? profile?.display_name || ''
    : atomDisplayName
  const profileImageUrl = shouldUseContext
    ? profile?.image_url || ''
    : atomProfileImageUrl
  const apiKeys = shouldUseContext ? getAvailableAPIKeys(profile) : atomApiKeys

  // Get IDs from profile
  const userId = profile?.user_id
  const profileId = profile?.id

  const setDisplayName = useCallback(
    (value: SetStateAction<string>) => {
      if (!shouldUseContext) {
        setAtomDisplayName(value)
        return
      }

      setProfile((currentProfile) => {
        if (!currentProfile) {
          return currentProfile
        }

        return {
          ...currentProfile,
          display_name: resolveStateAction(
            value,
            currentProfile.display_name || ''
          )
        }
      })
    },
    [setAtomDisplayName, setProfile, shouldUseContext]
  )

  const setProfileImageUrl = useCallback(
    (value: SetStateAction<string>) => {
      if (!shouldUseContext) {
        setAtomProfileImageUrl(value)
        return
      }

      setProfile((currentProfile) => {
        if (!currentProfile) {
          return currentProfile
        }

        return {
          ...currentProfile,
          image_url: resolveStateAction(value, currentProfile.image_url || '')
        }
      })
    },
    [setAtomProfileImageUrl, setProfile, shouldUseContext]
  )

  // Update profile data
  const updateProfileData = useCallback(
    async (updates: Partial<Tables<'profiles'>>) => {
      if (!profileId) {
        toast.error('You must be logged in to update your profile')
        return false
      }

      try {
        const updatedProfile = await updateProfile(profileId, updates)

        if (updatedProfile) {
          setProfile(updatedProfile)
          toast.success('Profile updated successfully')
          return true
        }

        return false
      } catch (error: any) {
        console.error('Error updating profile:', error)
        toast.error(error?.message || 'Failed to update profile')
        return false
      }
    },
    [profileId, setProfile]
  )

  // Update API key
  const updateApiKey = useCallback(
    async (provider: string, key: string) => {
      // Convert provider name to database field name
      const fieldMap: Record<string, string> = {
        openai: 'openai_api_key',
        anthropic: 'anthropic_api_key',
        google: 'google_gemini_api_key',
        azure: 'azure_openai_api_key',
        mistral: 'mistral_api_key',
        groq: 'groq_api_key',
        perplexity: 'perplexity_api_key',
        openrouter: 'openrouter_api_key'
      }

      const field = fieldMap[provider]
      if (!field || !profileId) return false

      // Update profile with new API key
      return updateProfileData({ [field]: key })
    },
    [profileId, updateProfileData]
  )

  // Get available API keys
  const getProfileApiKeys = useCallback(() => {
    if (!profile) return {}

    return getAvailableAPIKeys(profile)
  }, [profile])

  // Toggle profile settings
  const toggleProfileSettings = useCallback(
    (tab = '') => {
      setIsProfileSettingsOpen((prev) => (prev === tab ? '' : tab))
    },
    [setIsProfileSettingsOpen]
  )

  // Close profile settings
  const closeProfileSettings = useCallback(() => {
    setIsProfileSettingsOpen('')
  }, [setIsProfileSettingsOpen])

  // Sign out
  const signOut = useCallback(async () => {
    try {
      await logoutFromClient(supabase)
      toast.success('Signed out successfully')
      redirectToLoginAfterLogout()
      return true
    } catch (error: unknown) {
      console.error('Error signing out:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to sign out')
      return false
    }
  }, [])

  return {
    // State
    profile,
    displayName,
    profileImageUrl,
    isProfileSettingsOpen,
    apiKeys,

    // Computed values
    profileImageUrlOrPlaceholder: profileImageUrl || '/profile.png',
    isLoggedIn: !!userId,
    isAuthenticated: !!userId && !!profile,
    hasApiKeys: Object.keys(getProfileApiKeys()).length > 0,

    // Actions
    updateProfileData,
    updateApiKey,
    toggleProfileSettings,
    closeProfileSettings,
    signOut,
    setDisplayName,
    setProfileImageUrl,
    getProfileApiKeys
  }
}
